import {
  BREADCRUMBS_POSITION_OPTIONS,
  CONTAINER_OPTIONS,
  CONTENT_LAYOUT_OPTIONS,
  HEADING_ALIGNMENT_OPTIONS,
  MODULE_FILL_TYPE_OPTIONS,
  MODULE_FLOW_DIRECTION_OPTIONS,
  TITLE_SIZE_OPTIONS,
  TITLE_TYPE_OPTIONS,
} from '@/common/constants';
import { useModuleStyles } from '@/common/hooks';
import { BaseModuleProps } from '@/common/types';
import { HeadingBlock, HeadingBlockProps } from '@/components/blocks/HeadingBlock/HeadingBlock';
import { Breadcrumbs, BreadcrumbsProps } from '@/components/Breadcrumbs/Breadcrumbs';
import classNames from 'classnames';
import React, { useEffect, useId, useRef } from 'react';

import styles from './Form.module.scss';

const HUBSPOT_EMBED_URL = 'https://js.hsforms.net/forms/embed/v2.js';
const HUBSPOT_FORM_ID_PATTERN = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;
const HUBSPOT_REGION_PATTERN = /^[A-Za-z0-9-]+$/;

interface HubSpotFormConfig {
  region: string;
  portalId: string;
  formId: string;
  redirectUrl?: string;
  onFormSubmit?: (_form: unknown) => void;
}

interface HubSpotFormOptions extends HubSpotFormConfig {
  target: string;
}

interface HubSpot {
  forms: {
    create: (_options: HubSpotFormOptions) => void;
  };
}

interface HubSpotEmbed {
  forms?: {
    create?: unknown;
  };
}

function getHubSpotWindow(): typeof window & { hbspt?: HubSpotEmbed } {
  return window as typeof window & { hbspt?: HubSpotEmbed };
}

function getLoadedHubSpot(): HubSpot | undefined {
  const hubSpot = getHubSpotWindow().hbspt;
  return typeof hubSpot?.forms?.create === 'function' ? (hubSpot as HubSpot) : undefined;
}

let hubSpotLoadPromise: Promise<HubSpot> | undefined;

function readHubSpotOption(htmlContent: string, name: string): string | undefined {
  const pattern = new RegExp(`(?:[,{]\\s*)${name}\\s*:\\s*(["'])([^"']+)\\1`);
  return pattern.exec(htmlContent)?.[2];
}

function readHubSpotConfig(htmlContent: string): HubSpotFormConfig | undefined {
  if (!htmlContent.includes('hbspt.forms.create')) {
    return undefined;
  }

  const region = readHubSpotOption(htmlContent, 'region');
  const portalId = readHubSpotOption(htmlContent, 'portalId');
  const formId = readHubSpotOption(htmlContent, 'formId');
  const redirectUrl = readHubSpotOption(htmlContent, 'redirectUrl');

  if (
    !region ||
    !portalId ||
    !formId ||
    !HUBSPOT_REGION_PATTERN.test(region) ||
    !/^\d+$/.test(portalId) ||
    !HUBSPOT_FORM_ID_PATTERN.test(formId)
  ) {
    return undefined;
  }

  const config: HubSpotFormConfig = { region, portalId, formId };
  if (redirectUrl) {
    config.redirectUrl = redirectUrl;
  }
  if (htmlContent.includes('form_submission_url')) {
    config.onFormSubmit = (form) => {
      const element = form instanceof Element ? form : (form as { 0?: unknown })?.[0];
      if (!(element instanceof Element)) {
        return;
      }
      const input = element.querySelector<HTMLInputElement>('input[name="form_submission_url"]');
      if (!input) {
        return;
      }
      input.value = window.location.href;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
  }
  return config;
}

function removeScripts(htmlContent: string): string {
  return htmlContent.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');
}

function getHubSpotTargetId(componentId: string, htmlContent: string): string {
  let hash = 0;
  for (let index = 0; index < htmlContent.length; index += 1) {
    hash = (hash * 31 + htmlContent.charCodeAt(index)) >>> 0;
  }
  return `hubspot-form-${componentId.replace(/:/g, '')}-${hash}`;
}

function loadHubSpot(): Promise<HubSpot> {
  const loadedHubSpot = getLoadedHubSpot();
  if (loadedHubSpot) {
    return Promise.resolve(loadedHubSpot);
  }
  if (hubSpotLoadPromise) {
    return hubSpotLoadPromise;
  }

  hubSpotLoadPromise = new Promise<HubSpot>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = HUBSPOT_EMBED_URL;
    script.async = true;
    script.onload = () => {
      const hubSpot = getLoadedHubSpot();
      hubSpotLoadPromise = undefined;
      hubSpot ? resolve(hubSpot) : reject(new Error('HubSpot did not load'));
    };
    script.onerror = () => reject(new Error('HubSpot failed to load'));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    hubSpotLoadPromise = undefined;
    throw error;
  });

  return hubSpotLoadPromise;
}

export interface FormProps extends BaseModuleProps {
  /** HTML content to be dynamically inserted */
  htmlContent: string;
  breadcrumbs?: BreadcrumbsProps;
}

/**
 * Forms module component
 * @param props - The props for the Forms module
 * @returns The Forms module component
 */
export const Form: React.FC<FormProps> = ({
  anchorTag = undefined,
  container = CONTAINER_OPTIONS.UNCONTAINED,
  fillType = MODULE_FILL_TYPE_OPTIONS.BASE,
  gradientFillToken,
  layout = CONTENT_LAYOUT_OPTIONS.CONTENT_TRAILING,
  eyebrow,
  title,
  titleSize = TITLE_SIZE_OPTIONS.LARGE,
  titleType = TITLE_TYPE_OPTIONS.H2,
  bodyRichTextAsHTML,
  flowDirection = MODULE_FLOW_DIRECTION_OPTIONS.ROW,
  alignment = HEADING_ALIGNMENT_OPTIONS.START,
  ctas,
  caption,
  htmlContent,
  isFirstModule,
  breadcrumbs,
}) => {
  const hubSpotTargetRef = useRef<HTMLDivElement>(null);
  const hubSpotConfig = readHubSpotConfig(htmlContent);
  const hubSpotTargetId = getHubSpotTargetId(useId(), htmlContent);
  const headingConfig: HeadingBlockProps = {
    eyebrow,
    title,
    titleSize,
    titleType,
    bodyRichTextAsHTML,
    alignment,
    ctas,
    caption,
  };
  const showBreadcrumbs =
    isFirstModule && breadcrumbs && breadcrumbs.position === BREADCRUMBS_POSITION_OPTIONS.PAGE_TOP;
  const breadcrumbStyles = showBreadcrumbs ? styles.with_breadcrumb : '';

  // Use the common module styles hook
  const { sectionStyles, moduleBaseStyles } = useModuleStyles({
    container,
    fillType,
    gradientFillToken,
    flowDirection,
    layout,
    moduleClassName: 'module-forms',
    isFirstModule,
    styles,
  });

  useEffect(() => {
    const hubSpotConfig = readHubSpotConfig(htmlContent);
    const target = hubSpotTargetRef.current;
    if (!hubSpotConfig || !target) {
      return;
    }

    let cancelled = false;
    target.replaceChildren();
    void loadHubSpot()
      .then((hubSpot) => {
        if (!cancelled) {
          hubSpot.forms.create({ ...hubSpotConfig, target: `#${hubSpotTargetId}` });
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      target.replaceChildren();
    };
  }, [htmlContent, hubSpotTargetId]);

  return (
    <section className={classNames(sectionStyles, breadcrumbStyles)} id={anchorTag}>
      {showBreadcrumbs && <Breadcrumbs {...breadcrumbs} className={styles.module_breadcrumbs} />}
      <div className={moduleBaseStyles}>
        <HeadingBlock className={styles['form-heading-block']} {...headingConfig} />
        <div className="mlp-webapp-form">
          <div dangerouslySetInnerHTML={{ __html: removeScripts(htmlContent) }} />
          {hubSpotConfig && <div ref={hubSpotTargetRef} id={hubSpotTargetId} />}
        </div>
      </div>
    </section>
  );
};
